// @ts-nocheck
import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination
} from '@mui/material';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

// Extremely simplified interface with only primitive types
interface CitationAnalyticsProps {
  clientId: string;
  analyticsData: any; 
  recentCitations: any[];
  loadingRecent: boolean;
  recentError: string | null;
  currentPage: number;
  totalPages: number;
  onPageChange: (event: any, value: number) => void;
  totalRecentQueryCount: number;
}

const CitationAnalytics: React.FC<CitationAnalyticsProps> = (props) => {
  // Destructure all props carefully using defaults for everything
  const {
    clientId = '',
    analyticsData = null,
    recentCitations = [],
    loadingRecent = false,
    recentError = null,
    currentPage = 1,
    totalPages = 1,
    onPageChange = () => {},
    totalRecentQueryCount = 0
  } = props;

  console.log('[DEBUG] CitationAnalytics rendering with props:', {
    hasClientId: !!clientId,
    hasAnalyticsData: !!analyticsData,
    recentCitationsCount: recentCitations?.length || 0,
    loadingRecent,
    hasRecentError: !!recentError,
    currentPage,
    totalPages,
    totalRecentQueryCount
  });
  
  // Move useMemo before any conditional returns to follow React Hook rules
  // Simplify citation data 
  const simplifiedCitations = React.useMemo(() => {
    // Safety check
    if (!Array.isArray(recentCitations)) {
      console.warn('[DEBUG] recentCitations is not an array:', recentCitations);
      return [];
    }

    return recentCitations.map(citation => {
      // Safety check for citation object
      if (!citation || typeof citation !== 'object') {
        console.warn('[DEBUG] Invalid citation record, skipping:', citation);
        return null;
      }

      try {
        // Extract only the data we need, convert everything to strings/numbers
        return {
          id: String(citation.id || ''),
          query: String(citation.query || ''),
          citationCount: Number(citation.citation_count || 0),
          date: citation.timestamp ? new Date(citation.timestamp).toLocaleDateString() : 'Unknown date'
        };
      } catch (e) {
        console.error('[DEBUG] Error processing citation:', e);
        return null;
      }
    }).filter(Boolean); // Remove null items
  }, [recentCitations]);

  // Safety check for analytics data
  if (!analyticsData) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Core analytics data is not available.
      </Alert>
    );
  }

  // Extract data safely
  const totalCitations = analyticsData.total_citations || 0;
  const topSources = Array.isArray(analyticsData.top_sources) ? analyticsData.top_sources : [];
  const citationsByDay = Array.isArray(analyticsData.time_data) ? analyticsData.time_data : [];

  // Calculate average 
  const totalRecentQueries = recentCitations?.length || 0;
  const citationSum = recentCitations?.reduce((sum, citation) => {
    const count = Number(citation?.citation_count || 0);
    return sum + count;
  }, 0) || 0;
  const avgCitationsRecent = totalRecentQueries > 0 ? (citationSum / totalRecentQueries) : 0;

  // Chart data matching Daily Usage Trend design exactly
  const chartData = {
    labels: citationsByDay.map(item => {
      try {
        return new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } catch (e) {
        return 'Invalid date';
      }
    }),
    datasets: [{
      label: 'Citations',
      data: citationsByDay.map(item => Number(item.count || 0)),
      backgroundColor: 'rgba(234, 179, 8, 0.8)',
      borderColor: 'rgba(234, 179, 8, 1)',
      borderWidth: 1,
    }]
  };

  const chartOptions = { 
    responsive: true, 
    maintainAspectRatio: false, 
    scales: { 
      y: { 
        beginAtZero: true 
      } 
    } 
  };

  return (
    <div className="space-y-6">
      {/* Modern Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Citations Card */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 text-center transition-all duration-300 hover:shadow-xl">
          <div className="text-4xl font-bold mb-2" style={{ color: '#065BDF' }}>
            {totalCitations}
          </div>
          <div className="text-gray-600 text-sm font-medium uppercase tracking-wide">
            Total Citations
          </div>
        </div>

        {/* Total Queries Card */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 text-center transition-all duration-300 hover:shadow-xl">
          <div className="text-4xl font-bold mb-2" style={{ color: '#065BDF' }}>
            {totalRecentQueryCount}
          </div>
          <div className="text-gray-600 text-sm font-medium uppercase tracking-wide">
            Total Queries
          </div>
        </div>

        {/* Average Citations Card */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 text-center transition-all duration-300 hover:shadow-xl">
          <div className="text-4xl font-bold mb-2" style={{ color: '#065BDF' }}>
            {avgCitationsRecent.toFixed(1)}
          </div>
          <div className="text-gray-600 text-sm font-medium uppercase tracking-wide">
            Avg. Citations / Query
          </div>
        </div>
      </div>
      
      {/* Modern Chart Card */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Citations by Day</h3>
        <div className="h-64">
          {citationsByDay.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <div className="text-lg font-medium mb-2">No Citation Data</div>
              <div className="text-sm text-center">
                Citation analytics will appear here once you have citation data.
              </div>
            </div>
          ) : (
            <Bar 
              data={chartData}
              options={chartOptions}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CitationAnalytics;